import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ErrorResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { Guid } from "guid-typescript";
import { ChangeTextblockTemplateModel, TextBlockRequest, TextBlockResponse, TextblockTemplateModelForList, TextblockTemplateSliceModel, TextblockTemplatesDropdownLists, TextBlockResponseForDocuments } from "./textblock-template-interfaces";
import { GetTextFromHtml } from "shared/utils/common";
import { API_BASE_URL as baseUri } from "config/env";

export const GetActiveTextblockTemplates = createAsyncThunk<TextblockTemplateModelForList[], void, { rejectValue: RejectedValue }>(
    'Slice/TextblockTemplateSlice/GetActiveTextblockTemplates',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/textblocks/getall', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TextblockTemplateModelForList[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInactiveTextblockTemplates = createAsyncThunk<TextblockTemplateModelForList[], void, { rejectValue: RejectedValue }>(
    'Slice/TextblockTemplateSlice/GetInactiveTextblockTemplates',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/textblocktemplate/getallinactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TextblockTemplateModelForList[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTextblockTemplatesByDocumentType = createAsyncThunk<TextBlockResponseForDocuments[], string, { rejectValue: RejectedValue }>(
    'Slice/TextblockTemplateSlice/GetTextblockTemplatesByDocumentType',
    async (documentTypeId: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/textblocks/getbydocumenttype?documentTypeId=' + documentTypeId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TextBlockResponseForDocuments[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTextblocksTemplatesCreateData = createAsyncThunk<TextblockTemplatesDropdownLists, void, { rejectValue: RejectedValue }>(
    'Slice/TextblockTemplateSlice/GetCreateData',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/textblocks/getcreatedata', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TextblockTemplatesDropdownLists;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTextblockTemplate = createAsyncThunk<TextBlockResponse, Guid, { rejectValue: RejectedValue }>(
    'Slice/TextblockTemplateSlice/GetTextblockTemplate',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/textblocks/get?textBlockTemplateId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as TextBlockResponse;
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteTextblockTemplate = createAsyncThunk<ChangeTextblockTemplateModel, Guid, { rejectValue: RejectedValue }>(
    'Slice/TextblockTemplateSlice/DeleteTextblockTemplate',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/textblocks/delete?textBlockId=' + id, {
            method: "DELETE",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as ChangeTextblockTemplateModel;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateTextblockTemplate = createAsyncThunk<ChangeTextblockTemplateModel, Guid, { rejectValue: RejectedValue }>(
    'Slice/TextblockTemplateSlice/ReactivateTextblockTemplate',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/textblocktemplate/reactivate?projectLevelTextblockTemplateId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as ChangeTextblockTemplateModel;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateTextblockTemplate = createAsyncThunk<ChangeTextblockTemplateModel, TextBlockRequest, { rejectValue: RejectedValue }>(
    'Slice/TextblockTemplateSlice/CreateTextblockTemplate',
    async (template: TextBlockRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/textblocks/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(template)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ChangeTextblockTemplateModel;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateTextblockTemplate = createAsyncThunk<ChangeTextblockTemplateModel, TextBlockRequest, { rejectValue: RejectedValue }>(
    'Slice/TextblockTemplateSlice/UpdateTextblockTemplate',
    async (template: TextBlockRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/textblocks/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(template)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ChangeTextblockTemplateModel;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

const initialValue: TextblockTemplateSliceModel = {
    statusCode: "",
    isLoading: false,
    isModalLoading: false,
    message: "",
    showNotification: false,
    listOfActiveTemplates: [],
    listOfInactiveTemplates: [],
    listOfTextblocksByType: [],
    dropdownLists: null,
    template: null,
}

export const TextblockTemplateSlice = createSlice({
    name: 'TextblockTemplateSlice',
    initialState: initialValue,
    reducers: {
        resetTextblockTemplateSlice: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.showNotification = false;
            state.message = '';
            state.listOfActiveTemplates = [];
            state.listOfInactiveTemplates = [];
            state.listOfTextblocksByType = [];
            state.dropdownLists = null;
            state.template = null;
        },
        resetTextblockTemplate: (state) => {
            state.template = null;
            state.isModalLoading = false;
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
    },
    extraReducers(builder) {
        //Get Active TextblockTemplates
        builder
            .addCase(GetActiveTextblockTemplates.fulfilled, (state, action: PayloadAction<TextblockTemplateModelForList[]>) => {
                state.isLoading = false;
                state.message = '';
                state.listOfActiveTemplates = action.payload;
            })
            .addCase(GetActiveTextblockTemplates.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActiveTextblockTemplates.pending, state => {
                state.isLoading = true;
            })
        //Get Inactive TextblockTemplates
        builder
            .addCase(GetInactiveTextblockTemplates.fulfilled, (state, action: PayloadAction<TextblockTemplateModelForList[]>) => {
                state.isLoading = false;
                state.message = '';
                state.listOfActiveTemplates = action.payload;
            })
            .addCase(GetInactiveTextblockTemplates.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetInactiveTextblockTemplates.pending, state => {
                state.isLoading = true;
            })

        //Get Textblock Templates By Document Type
        builder
            .addCase(GetTextblockTemplatesByDocumentType.fulfilled, (state, action: PayloadAction<TextBlockResponseForDocuments[]>) => {
                state.isLoading = false;
                state.message = '';
                state.listOfTextblocksByType = action.payload;
            })
            .addCase(GetTextblockTemplatesByDocumentType.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTextblockTemplatesByDocumentType.pending, state => {
                state.isLoading = true;
            })
        //Get Templates Create Data
        builder
            .addCase(GetTextblocksTemplatesCreateData.fulfilled, (state, action: PayloadAction<TextblockTemplatesDropdownLists>) => {
                state.isModalLoading = false;
                state.message = '';
                state.dropdownLists = action.payload;
            })
            .addCase(GetTextblocksTemplatesCreateData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTextblocksTemplatesCreateData.pending, state => {
                state.isModalLoading = true;
            })
        //Get TextblockTemplate
        builder
            .addCase(GetTextblockTemplate.fulfilled, (state, action: PayloadAction<TextBlockResponse>) => {
                state.isLoading = false;
                state.message = '';
                state.template = action.payload;
            })
            .addCase(GetTextblockTemplate.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTextblockTemplate.pending, state => {
                state.isLoading = true;
            })
        //Update TextblockTemplate
        builder
            .addCase(UpdateTextblockTemplate.fulfilled, (state, action: PayloadAction<ChangeTextblockTemplateModel>) => {
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The template is successfully updated.';
                state.showNotification = true;
            })
            .addCase(UpdateTextblockTemplate.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateTextblockTemplate.pending, state => {
                state.isModalLoading = true;
            })
        //Delete TextblockTemplate
        builder
            .addCase(DeleteTextblockTemplate.fulfilled, (state, action: PayloadAction<ChangeTextblockTemplateModel>) => {
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The template is successfully deleted.';
                state.showNotification = true;
            })
            .addCase(DeleteTextblockTemplate.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteTextblockTemplate.pending, state => {
                state.isModalLoading = true;
            })
        //Reactivate TextblockTemplate
        builder
            .addCase(ReactivateTextblockTemplate.fulfilled, (state, action: PayloadAction<ChangeTextblockTemplateModel>) => {
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The template is successfully reactivated.';
                state.showNotification = true;
            })
            .addCase(ReactivateTextblockTemplate.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(ReactivateTextblockTemplate.pending, state => {
                state.isModalLoading = true;
            })
        //Create TextblockTemplate
        builder
            .addCase(CreateTextblockTemplate.fulfilled, (state, action: PayloadAction<ChangeTextblockTemplateModel>) => {
                state.message = 'The template is successfully created.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
            })
            .addCase(CreateTextblockTemplate.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateTextblockTemplate.pending, state => {
                state.isModalLoading = true;
            })
    }
});

export const { resetTextblockTemplate, resetTextblockTemplateSlice, resetStatusCode } = TextblockTemplateSlice.actions;
export default TextblockTemplateSlice.reducer;